/**
 * Service module for handling building-related API calls.
 * Provides functions to fetch building lists, data, and monthly readings.
 */

// API configuration
const API_URL = "https://vesimittari.azurewebsites.net/api/rajapinta";
const API_KEY = "VITE_API_KEY";

/**
 * Fetches the list of buildings associated with a user.
 * @param {string} userEmail - The email address of the user
 * @returns {Promise<Array>} A promise that resolves to an array of building data
 * @throws {Error} If the API request fails
 */
export const fetchBuildingList = async (userEmail) => {
  const response = await fetch(API_URL, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "x-function-key": API_KEY,
    },
    body: JSON.stringify({
      c: "getbuildinglist",
      p: [userEmail],
    }),
  });

  if (!response.ok) throw new Error("Failed to fetch building data");
  const result = await response.json();
  return JSON.parse(result.t);
};

/**
 * Fetches detailed building data for a specific time period.
 * @param {string} guid - The unique identifier of the building
 * @param {string} startDate - The start date for the data range
 * @param {string} endDate - The end date for the data range
 * @returns {Promise<Object|null>} A promise that resolves to the building data or null if no data is found
 * @throws {Error} If the API request fails
 */
export const fetchBuildingData = async (guid, startDate, endDate) => {
  const response = await fetch(API_URL, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "x-function-key": API_KEY,
    },
    body: JSON.stringify({
      c: "getbuildingdata",
      p: [guid, "0", startDate, endDate],
    }),
  });

  if (!response.ok) throw new Error("Failed to fetch data");
  const result = await response.json();
  return result.t ? JSON.parse(result.t) : null;
};

/**
 * Fetches monthly reading data for a specific building.
 * @param {string} guid - The unique identifier of the building
 * @returns {Promise<Object|null>} A promise that resolves to the monthly data or null if no data is found
 * @throws {Error} If the API request fails
 */
export const fetchBuildingMonthlyData = async (guid) => {
  const response = await fetch(API_URL, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "x-function-key": API_KEY,
    },
    body: JSON.stringify({
      c: "getmonthlydata",
      p: [guid],
    }),
  });

  if (!response.ok) throw new Error("Failed to fetch reading data");
  const result = await response.json();
  return result.t ? JSON.parse(result.t) : null;
};

/**
 * Fetches daily reading data for a specific building.
 * @param {string} guid - The unique identifier of the building
 * @returns {Promise<Object|null>} A promise that resolves to the daily data or null if no data is found
 * @throws {Error} If the API request fails
 */
export const fetchBuildingDailyData = async (guid) => {
  const response = await fetch(API_URL, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "x-function-key": API_KEY,
    },
    body: JSON.stringify({
      c: "getdailydata",
      p: [guid],
    }),
  });

  if (!response.ok) throw new Error("Failed to fetch daily data");
  const result = await response.json();
  return result.t ? JSON.parse(result.t) : null;
};
